"use client";

import Link from "next/link";
import Image from "next/image";
import { useSiteContext } from "@/components/providers/SiteContextProvider";
import { getTranslations } from "@/lib/i18n";
import { getLocalizedField } from "@/lib/localized-field";
import { slugify } from "@/lib/slugify";
import { parseI18nTitleNL } from "@/lib/voucher-resolver";
import { formatPrice } from "@/lib/format-price";
import { convertEurTo } from "@/lib/currency-convert";
import { imgUrl } from "@/lib/api";
import { CARD_IMAGE_QUALITY } from "@/lib/image-quality";
import { fireClickBeacon } from "@/components/personalization/RecordDealClick";
import { resolveDisplayPrice } from "@/lib/resolve-display-price";
import { dealHasActivity } from "@/lib/booking/voucher-stay-cost";
import { hotelIdsOf } from "@/lib/hotel-count";
import { useFixedDateVoucherUnavailable, useVoucherSaleClosed } from "@/lib/use-fixed-date-voucher-unavailable";

// C21 fix (Fable5 fix-campagne, HIGH-tier): route through the canonical
// resolveDisplayPrice (voucherRow:true — this strip only ever renders
// catalogue vouchers) instead of a hand-copied chain that duplicated
// VoucherListClient's getPrice() byte-for-byte, so the two surfaces can
// never independently drift.
function getDisplayPrice(voucher) {
  return resolveDisplayPrice(voucher, { voucherRow: true });
}

export default function CategoryVoucherCard({ voucher, source = "category_voucher_strip", position, variant = "card" }) {
  const fixedDateVoucherUnavailable =
    useFixedDateVoucherUnavailable(voucher);
  // Fixronde 3 #1920: also gone after its last sales day (182), on the
  // reactive clock, also when the backend still returns it as active.
  const saleClosed = useVoucherSaleClosed(voucher);
  const { currency, effectiveRate, locale } = useSiteContext();
  const { t } = getTranslations(locale);
  if (fixedDateVoucherUnavailable || saleClosed) return null;
  const toCurrency = (eur) => convertEurTo(eur, currency, effectiveRate);

  const localizedTitle =
    getLocalizedField(voucher, "title", locale) ||
    getLocalizedField(voucher, "name", locale) ||
    voucher.title ||
    voucher.name ||
    "";

  const imgSrc = imgUrl(voucher.deals_img) || "/images/placeholder-deal.svg";

  const price = getDisplayPrice(voucher);
  const hasHotel = hotelIdsOf(voucher).length > 0;
  const hasActivity = dealHasActivity(voucher);

  const detailHref = `/vouchers/${
    slugify(parseI18nTitleNL(voucher.package_info || "")) ||
    slugify(localizedTitle || voucher.title || voucher.name || "")
  }`;

  function handleClick() {
    fireClickBeacon(voucher?.deals_id ?? voucher?.id, source, position);
  }

  // 2026-08-30 above-the-fold fix (Dennis, regie-sessie AskUserQuestion-
  // keuze): the "card" and "compact" (variant="compact") layouts below
  // share this EXACT price + per-2-personen-unit block and this EXACT CTA
  // — never re-derive the price or the unit text per variant, per the
  // owner rule in feedback_voucher_prijs_per_twee_personen_communiceren.md.
  const priceBlock = (
    <div>
      <p className="text-[10px] text-gray-500 leading-tight">{t("vouchers.today_for", "Vandaag voor")}</p>
      <p className="text-xl font-bold text-gray-900 leading-tight whitespace-nowrap">
        {formatPrice(toCurrency(price), { currency, includeSymbol: true, locale })}
      </p>
      {/* Owner rule 2026-08-15 (feedback_voucher_prijs_per_twee_personen_communiceren.md):
          a voucher is always ONE price for TWO people -- must be explicit,
          same key + placement as the sibling VoucherListClient card. */}
      <p className="text-[10px] text-gray-500 leading-tight mt-0.5">{t("vouchers.two_persons")}</p>
    </div>
  );

  const ctaBlock = (
    <span
      className="text-[11px] font-semibold px-2.5 py-1.5 rounded-md text-white"
      style={{ backgroundColor: "#0070b8" }}
    >
      {t("listing.buy_voucher", "Koop voucher")}
    </span>
  );

  if (variant === "compact") {
    return (
      <Link
        href={detailHref}
        onClick={handleClick}
        className="group relative flex items-center gap-3 bg-white overflow-hidden hover:shadow-md transition-shadow p-2"
        style={{ borderRadius: "10px", boxShadow: "0 2px 6px rgba(0,0,0,0.06)" }}
      >
        <div className="relative w-24 h-24 sm:w-28 sm:h-28 flex-shrink-0 overflow-hidden rounded-md">
          <Image
            src={imgSrc}
            alt={localizedTitle || "Voucher"}
            fill
            sizes="(min-width: 640px) 112px, 96px"
            quality={CARD_IMAGE_QUALITY}
            className="object-cover group-hover:scale-105 transition-transform duration-300"
          />
        </div>

        {/* 2026-08-30 regie-fix (mobiel 375px): thumb + prijsblok + CTA
            (allebei flex-shrink-0) lieten de titel vrijwel 0 breedte over
            op smalle schermen. Vanaf hier is dit een responsive kolom:
            titel bovenin, prijs+CTA daaronder op mobiel; vanaf sm: weer
            naast elkaar op een rij, zodat het desktop-uiterlijk (titel
            midden, prijs+knop rechts) ongewijzigd blijft. */}
        <div className="flex-1 min-w-0 flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3">
          <h3 className="min-w-0 font-bold text-[13.5px] text-gray-900 leading-snug line-clamp-2 group-hover:text-blue-600 transition-colors sm:flex-1">
            {localizedTitle}
          </h3>

          <div className="flex items-center justify-between gap-3 sm:justify-start sm:flex-shrink-0">
            {priceBlock}
            {ctaBlock}
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      href={detailHref}
      onClick={handleClick}
      className="group relative flex flex-col bg-white overflow-hidden hover:shadow-lg transition-shadow"
      style={{ borderRadius: "10px", boxShadow: "0 4px 10px rgba(0,0,0,0.08)" }}
    >
      <div className="relative w-full aspect-[16/10] overflow-hidden">
        <Image
          src={imgSrc}
          alt={localizedTitle || "Voucher"}
          fill
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
          quality={CARD_IMAGE_QUALITY}
          className="object-cover group-hover:scale-105 transition-transform duration-300"
        />
        <div
          className="absolute top-3 right-3 flex items-center gap-1"
          style={{ backgroundColor: "#0070b8", borderRadius: "4px", padding: "4px 8px" }}
        >
          <img src="/images/icons/ticket-white.svg" alt="" width={12} height={12} />
          <span style={{ color: "white", fontSize: "10px", fontWeight: 600, letterSpacing: "0.02em" }}>
            {t("listing.voucher_badge", "Voucher")}
          </span>
        </div>
      </div>

      <div className="p-3.5 flex flex-col gap-2 flex-1">
        <div className="flex items-center gap-1.5">
          {hasHotel && <img src="/images/icons/bed-icon.svg" alt="" width={20} height={14} />}
          {hasHotel && hasActivity && <img src="/images/icons/plus-icon.svg" alt="" width={9} height={10} />}
          {hasActivity && <img src="/images/icons/ticket-icon.svg" alt="" width={18} height={15} />}
        </div>

        <h3 className="font-bold text-[14.5px] text-gray-900 leading-snug line-clamp-2 group-hover:text-blue-600 transition-colors">
          {localizedTitle}
        </h3>

        <div className="mt-auto pt-1 flex items-end justify-between">
          {priceBlock}
          {ctaBlock}
        </div>
      </div>
    </Link>
  );
}
